import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Page from "src/layouts/Page";
import ContentTable, {
	ContentTableItem,
	ContentTableColumn
} from "src/components/ContentTable/ContentTable";
import Pagination from "src/components/Pagination/Pagination";
import { ConfirmationModal } from "src/modals/ConfirmationModal/ConfirmationModal";
import { DEFAULT_ADMIN_PAGE_SIZE } from "src/constants";
import useDebounce from "src/hooks/useDebounce/useDebounce";
import useAdminGroups from "src/features/Groups/hooks/useAdminGroups";
import useAnnouncementsPaged from "../../hooks/useAnnouncementsPaged";
import useDeleteAnnouncement from "../../hooks/useDeleteAnnouncement";
import { mapAnnouncementsToContentTableItems } from "../../utils/mapAnnouncementsToContentTableItems";
import {
	getRelevantDate,
	getDateLabel,
	isDraftAnnouncement
} from "../../utils/announcementListHelpers";
import AnnouncementAudienceCell from "../../components/AnnouncementAudienceCell/AnnouncementAudienceCell";

type AnnouncementStatus = "draft" | "scheduled" | "sent" | "expired";
type StatusFilter = AnnouncementStatus | "all";

const statusConfig: Record<
	AnnouncementStatus,
	{ label: string; className: string }
> = {
	draft: { label: "Draft", className: "bg-muted text-muted-foreground" },
	scheduled: { label: "Scheduled", className: "bg-primary/10 text-primary" },
	sent: { label: "Sent", className: "bg-emerald-50 text-emerald-700" },
	expired: { label: "Expired", className: "bg-red-50 text-red-700" }
};

type NotificationType = "announcement" | "push";

const typeLabels: Record<NotificationType, string> = {
	announcement: "Announcement",
	push: "Push"
};

const statusFilterOptions: { value: StatusFilter; label: string }[] = [
	{ value: "all", label: "All statuses" },
	{ value: "draft", label: "Draft" },
	{ value: "scheduled", label: "Scheduled" },
	{ value: "sent", label: "Sent" },
	{ value: "expired", label: "Expired" }
];

const buildAnnouncementColumns = (
	groupNamesById: Map<number, string>
): ContentTableColumn[] => [
	{
		key: "type",
		label: "Type",
		width: "140px",
		render: (item) => {
			const type =
				(item.data?.notificationType as NotificationType) ?? "announcement";
			return <span className="text-sm">{typeLabels[type]}</span>;
		}
	},
	{
		key: "audience",
		label: "Audience",
		width: "160px",
		render: (item) => (
			<AnnouncementAudienceCell
				audienceType={
					(item.data?.audienceType as
						| "everyone"
						| "platform"
						| "groups"
						| "users") ?? "everyone"
				}
				audiencePlatform={
					item.data?.audiencePlatform as "ios" | "android" | "both" | null
				}
				audienceUserIds={item.data?.audienceUserIds as string[] | undefined}
				audienceGroupIds={item.data?.audienceGroupIds as number[] | undefined}
				groupNamesById={groupNamesById}
			/>
		)
	},
	{
		key: "date",
		label: "Date",
		render: (item) => (
			<span>
				<span className="text-muted-foreground text-xs">
					{getDateLabel(item)}
				</span>
				<br />
				{getRelevantDate(item)}
			</span>
		)
	},
	{
		key: "status",
		label: "Status",
		width: "120px",
		render: (item) => {
			const status = (item.data?.status as AnnouncementStatus) ?? "draft";
			const config = statusConfig[status];
			return (
				<span
					className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${config.className}`}
				>
					{config.label}
				</span>
			);
		}
	}
];

const Announcements = () => {
	const navigate = useNavigate();
	const [itemToDelete, setItemToDelete] = useState<ContentTableItem | null>(
		null
	);
	const [page, setPage] = useState(1);
	const [searchInput, setSearchInput] = useState("");
	const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
	const [filterGroupId, setFilterGroupId] = useState<
		number | "all" | "everyone"
	>("all");

	const debouncedSearch = useDebounce(searchInput, 300);

	useEffect(() => {
		setPage(1);
	}, [debouncedSearch, statusFilter, filterGroupId]);

	const {
		data: allGroups = [],
		isLoading: adminGroupsLoading,
		isError: adminGroupsError
	} = useAdminGroups();

	// If the currently-selected group disappears from adminGroups (deleted by
	// another admin while this page is open), reset the filter so the user
	// isn't stuck filtered to an invisible option. Skip while the groups query
	// is loading or has errored — adminGroups is then an empty fallback list
	// and we'd otherwise wipe the user's selection on every transient state.
	useEffect(() => {
		if (adminGroupsLoading || adminGroupsError) return;
		if (typeof filterGroupId !== "number") return;
		if (!allGroups.some((g) => Number(g.id) === filterGroupId)) {
			setFilterGroupId("all");
		}
	}, [allGroups, filterGroupId, adminGroupsLoading, adminGroupsError]);

	const audienceGroupIdParam =
		typeof filterGroupId === "number" ? filterGroupId : undefined;
	const onlyEveryoneParam = filterGroupId === "everyone";

	const { data, isLoading, isFetching } = useAnnouncementsPaged(
		page,
		DEFAULT_ADMIN_PAGE_SIZE,
		statusFilter === "all" ? undefined : statusFilter,
		debouncedSearch,
		audienceGroupIdParam,
		onlyEveryoneParam
	);
	const { mutate: deleteAnnouncement, isLoading: isDeleting } =
		useDeleteAnnouncement();

	const groupNamesById = useMemo(
		() => new Map(allGroups.map((g) => [Number(g.id), g.name])),
		[allGroups]
	);
	const announcementColumns = useMemo(
		() => buildAnnouncementColumns(groupNamesById),
		[groupNamesById]
	);

	const totalCount = data?.count ?? 0;
	const items = mapAnnouncementsToContentTableItems(data?.results ?? []);

	const statusFilterNode = (
		<label className="inline-flex items-center gap-2 text-sm text-muted-foreground">
			<span>Status</span>
			<select
				value={statusFilter}
				onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
				className="rounded-lg border border-border bg-white px-3 py-1.5 text-sm text-foreground shadow-xs focus:outline-none focus:ring-2 focus:ring-ring"
				aria-label="Filter announcements by status"
			>
				{statusFilterOptions.map((option) => (
					<option key={option.value} value={option.value}>
						{option.label}
					</option>
				))}
			</select>
		</label>
	);

	return (
		<Page>
			<ContentTable
				items={items}
				columns={announcementColumns}
				showCreatedBy={true}
				showLastUpdated={false}
				showOrder={false}
				showStatus={false}
				searchValue={searchInput}
				onSearchChange={setSearchInput}
				searchPlaceholder="Search announcements..."
				filterSlot={statusFilterNode}
				isLoading={isLoading || isFetching}
				onRowClick={(item) => navigate(`/announcements/${item.id}`)}
				onDelete={(item) => setItemToDelete(item)}
				canDelete={isDraftAnnouncement}
				createPath="/announcements/create"
				createLabel="New Announcement"
				searchRowExtra={
					<div className="flex items-center gap-2 text-sm">
						<label className="flex items-center gap-2 text-muted-foreground">
							<span>Filter by group</span>
							<select
								value={filterGroupId}
								onChange={(e) => {
									const v = e.target.value;
									if (v === "all" || v === "everyone") setFilterGroupId(v);
									else setFilterGroupId(Number(v));
								}}
								className="rounded-md border border-input bg-card px-3 py-1.5 text-sm text-foreground shadow-xs focus:outline-none focus:ring-2 focus:ring-ring"
								aria-label="Filter announcements by group"
							>
								<option value="all">All groups</option>
								<option value="everyone">Everyone (no restrictions)</option>
								{allGroups.map((g) => (
									<option key={g.id} value={Number(g.id)}>
										{g.name}
									</option>
								))}
							</select>
						</label>
						{adminGroupsError && (
							<span
								role="alert"
								className="text-xs text-destructive"
								title="Failed to load groups"
							>
								Couldn’t load groups
							</span>
						)}
					</div>
				}
			/>

			<Pagination
				page={page}
				pageSize={DEFAULT_ADMIN_PAGE_SIZE}
				totalCount={totalCount}
				onPageChange={setPage}
				itemLabel="announcements"
			/>

			<ConfirmationModal
				isOpen={itemToDelete !== null}
				title="Delete announcement?"
				description="This action cannot be undone."
				onCancel={() => {
					if (!isDeleting) setItemToDelete(null);
				}}
				cancelBtnProps={{ disabled: isDeleting }}
				onSubmit={() => {
					if (itemToDelete) {
						deleteAnnouncement(Number(itemToDelete.id), {
							onSuccess: () => setItemToDelete(null)
						});
					}
				}}
				submitBtnProps={{ isLoading: isDeleting }}
			/>
		</Page>
	);
};

export default Announcements;
